import { lazy } from 'react';

import { userPaths } from 'routes/constants/userPaths';

import { Navigate, Route } from 'react-router-dom';

import DashboardResolver from './DashboardResolver';
import EndUserFullFlowRoute from './guards/EndUserFullFlowRoute';
import EndUserTpaConnectionRoute from './guards/EndUserTpaConnectionRoute';
import ExpenseCertificationConsentRoute from './guards/ExpenseCertificationConsentRoute';
import UserPrivateRoute from './guards/UserPrivateRoute';
import UserPublicRoute from './guards/UserPublicRoute';

import HsaSuccessPage from '@/pages/confirmCashback/hsaSuccessPage';

const FTUEflow = lazy(() => import('components/ftueFlow'));
const TermsPage = lazy(() => import('pages/terms'));
const LinkExpired = lazy(() => import('pages/linkExpired'));
const EndUserHome = lazy(() => import('pages/endUserHome'));
const ExpenseCertificationPage = lazy(() => import('pages/expenseCertification'));
const ConfirmCashback = lazy(() => import('pages/confirmCashback'));
const ApprovePayoutPage = lazy(() => import('pages/confirmCashback/ApprovePayoutPage'));
const PayoutSetupLayout = lazy(() => import('pages/payoutSetup/PayoutSetupLayout'));
const PayoutSetupHubPage = lazy(() => import('pages/payoutSetup/PayoutSetupHubPage'));
const ConnectPayoutAccountStep = lazy(
  () => import('pages/payoutSetup/steps/ConnectPayoutAccountStep')
);
const ConnectPayoutAccountKycStep = lazy(
  () => import('pages/payoutSetup/steps/ConnectPayoutAccountKycStep')
);
const ConfirmHsaAccountStep = lazy(() => import('pages/payoutSetup/steps/ConfirmHsaAccountStep'));
const AchAuthorizationStep = lazy(() => import('pages/payoutSetup/steps/AchAuthorizationStep'));
const PayrollDeductionAuthStep = lazy(
  () => import('pages/payoutSetup/steps/PayrollDeductionAuthStep')
);
const OnboardingLayout = lazy(() => import('components/onboardingLayout'));
const EmailVerification = lazy(() => import('components/emailVerification'));
const OnboardingOtpVerification = lazy(() => import('components/onboardingOtpVerification'));
const PhoneVerification = lazy(() => import('components/phoneVerification'));
const CompleteProfile = lazy(() => import('pages/completeProfile'));
const PersonalDetails = lazy(() => import('pages/personalDetails'));
const FindExpensesConnectAccount = lazy(() => import('pages/findExpenses/ConnectAccount'));
const FindExpensesProviderList = lazy(() => import('pages/findExpenses/InsuranceProviderList'));
const FindExpensesConnectSuccess = lazy(() => import('pages/findExpenses/ConnectSuccess'));
const FindExpensesStreamConnectAuth = lazy(
  () => import('pages/findExpenses/StreamConnectAuthPage')
);
const NoExpenseFound = lazy(() => import('pages/noExpenseFound'));
const FinchConnection = lazy(() => import('pages/FinchConnection'));
const PiiVerificationPage = lazy(() => import('pages/pii-verification'));
const SignInEmail = lazy(() => import('components/signInEmail'));
const SignInPhone = lazy(() => import('components/signInPhone'));
const SignInOtp = lazy(() => import('components/signInOtp'));
const HsaPaydaysPage = lazy(() => import('pages/HSA Paydays'));
const ExpensesPage = lazy(() => import('pages/Expenses'));
const NoExpensesRemainingPage = lazy(() => import('pages/Expenses/NoExpensesRemaining'));
const MyAccountPage = lazy(() => import('pages/myAccount'));
const UpdateProfilePage = lazy(() => import('pages/myAccount/UpdateProfile'));
const HsaDetailsPage = lazy(() => import('pages/myAccount/HsaDetails'));
const HealthAccountsPage = lazy(() => import('pages/myAccount/HealthAccounts'));

const UserRouteTree = (
  <>
    <Route element={<UserPublicRoute />}>
      <Route path={userPaths.signIn} element={<SignInEmail />} />
      <Route path={userPaths.signInPhone} element={<SignInPhone />} />
      <Route path={userPaths.signInOtp} element={<SignInOtp />} />
      <Route path={userPaths.ftue} element={<FTUEflow />} />
      <Route path={userPaths.terms} element={<TermsPage />} />
      <Route path={userPaths.linkExpired} element={<LinkExpired />} />
    </Route>
    {/* Support ticket + OAuth callback: must work signed out and signed in (not under UserPublicRoute) */}
    <Route path={userPaths.piiVerification} element={<PiiVerificationPage />} />
    <Route path={userPaths.finchConnection} element={<FinchConnection />} />
    <Route path={userPaths.onboarding} element={<OnboardingLayout />}>
      <Route element={<UserPublicRoute />}>
        <Route index element={<Navigate to="email-verification" replace />} />
        <Route path="email-verification" element={<EmailVerification />} />
        <Route path="phone-verification" element={<PhoneVerification />} />
        <Route path="otp-verification" element={<OnboardingOtpVerification />} />
      </Route>
      <Route element={<UserPrivateRoute />}>
        <Route path="complete-profile" element={<CompleteProfile />} />
        <Route path="personal-details" element={<PersonalDetails />} />
        <Route element={<EndUserTpaConnectionRoute />}>
          <Route path="find-expenses" element={<FindExpensesConnectAccount />} />
          <Route path="find-expenses/success" element={<FindExpensesConnectSuccess />} />
          <Route path="find-expenses/providers" element={<FindExpensesProviderList />} />
          <Route
            path="find-expenses/providers/:providerId"
            element={<FindExpensesStreamConnectAuth />}
          />
        </Route>
      </Route>
    </Route>
    <Route element={<UserPrivateRoute />}>
      <Route path={userPaths.endUser} element={<EndUserHome />} />
      <Route path={userPaths.myAccount} element={<MyAccountPage />} />
      <Route path={userPaths.myAccountUpdateProfile} element={<UpdateProfilePage />} />
      <Route path={userPaths.myAccountHsaDetails} element={<HsaDetailsPage />} />
      <Route path={userPaths.myAccountHealthAccounts} element={<HealthAccountsPage />} />
      <Route path={userPaths.dashboard} element={<DashboardResolver />} />
      <Route element={<EndUserFullFlowRoute />}>
        <Route element={<ExpenseCertificationConsentRoute />}>
          <Route path={userPaths.expenseCertification} element={<ExpenseCertificationPage />} />
        </Route>
        <Route path={userPaths.confirmCashback} element={<ConfirmCashback />} />
        {/* Legacy Cash Out screen removed — keep path as redirect to HSA Paydays. */}
        <Route path={userPaths.cashOut} element={<Navigate to={userPaths.hsaPaydays} replace />} />
        <Route path={userPaths.payoutSetup} element={<PayoutSetupLayout />}>
          <Route index element={<PayoutSetupHubPage />} />
          <Route path="connect-payout-account" element={<ConnectPayoutAccountStep />} />
          <Route path="connect-payout-account/kyc" element={<ConnectPayoutAccountKycStep />} />
          <Route
            path="connect-payout-account/kyc/failed"
            element={<ConnectPayoutAccountKycStep />}
          />
          <Route path="confirm-hsa-account" element={<ConfirmHsaAccountStep />} />
          <Route path="ach-authorization" element={<AchAuthorizationStep />} />
          <Route path="payroll-deduction-auth" element={<PayrollDeductionAuthStep />} />
        </Route>
        <Route path={userPaths.approvePayout} element={<ApprovePayoutPage />} />
        <Route path={userPaths.hsaPaydays} element={<HsaPaydaysPage />} />
        <Route path={userPaths.expenses} element={<ExpensesPage />} />
        <Route path={userPaths.hsaSuccess} element={<HsaSuccessPage />} />
        <Route path={userPaths.noExpensesRemaining} element={<NoExpensesRemainingPage />} />
        <Route path={userPaths.noExpenseFound} element={<NoExpenseFound />} />
      </Route>
    </Route>
  </>
);

export default UserRouteTree;
